import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { PageHeader, Modal, Field, TextArea, Select, Button, StatusBadge } from "@/components/ui-kit";
import { toast } from "sonner";
import { Plus, Trash, BellRinging, CheckCircle, Circle } from "@phosphor-icons/react";

const empty = { title: "", note: "", kind: "follow_up", due_date: "", customer_id: "" };
const kindLabel = { follow_up: "Follow up", payment: "Payment", email: "Email", general: "General" };

export default function Reminders() {
  const [reminders, setReminders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => api.get("/reminders").then(({ data }) => setReminders(data)), []);
  useEffect(() => {
    load();
    api.get("/customers").then(({ data }) => setCustomers(data));
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reminders", { ...form, customer_id: form.customer_id || null });
      toast.success("Reminder created");
      setOpen(false);
      setForm(empty);
      load();
    } catch {
      toast.error("Failed to create reminder");
    }
  };

  const toggle = async (r) => {
    await api.put(`/reminders/${r.id}`, { done: !r.done });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/reminders/${id}`);
    toast.success("Reminder deleted");
    load();
  };

  const today = new Date().toISOString().slice(0, 10);
  const openR = reminders.filter((r) => !r.done);
  const overdue = openR.filter((r) => String(r.due_date).slice(0, 10) < today);
  const upcoming = openR.filter((r) => String(r.due_date).slice(0, 10) >= today);
  const done = reminders.filter((r) => r.done);

  const Row = ({ r, isOverdue }) => (
    <div data-testid={`reminder-${r.id}`} className="flex items-center justify-between px-4 py-3.5 rounded-xl border border-slate-200 bg-white hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-3.5">
        <button data-testid={`toggle-reminder-${r.id}`} onClick={() => toggle(r)} className="text-slate-300 hover:text-[#22C55E] transition-colors">
          {r.done ? <CheckCircle size={24} weight="fill" className="text-[#22C55E]" /> : <Circle size={24} weight="regular" />}
        </button>
        <div>
          <p className={`text-sm font-semibold ${r.done ? "line-through text-slate-400" : "text-slate-800"}`}>{r.title}</p>
          <p className="font-mono text-xs text-slate-400">
            {r.customer_name ? `${r.customer_name} · ` : ""}
            <span className={isOverdue ? "text-[#B91C1C] font-semibold" : ""}>{new Date(r.due_date).toLocaleDateString()}</span>
          </p>
          {r.note && <p className="text-xs text-slate-500 mt-0.5">{r.note}</p>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <StatusBadge status={r.kind} label={kindLabel[r.kind]} />
        <button data-testid={`delete-reminder-${r.id}`} onClick={() => remove(r.id)} className="text-slate-300 hover:text-red-500 transition-colors">
          <Trash size={16} weight="bold" />
        </button>
      </div>
    </div>
  );

  const Section = ({ title, items, isOverdue, accent }) =>
    items.length > 0 && (
      <div>
        <h3 className="font-heading font-bold text-sm uppercase tracking-wide mb-3 flex items-center gap-2" style={{ color: accent }}>
          {title} <span className="font-mono text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{items.length}</span>
        </h3>
        <div className="space-y-2">
          {items.map((r) => <Row key={r.id} r={r} isOverdue={isOverdue} />)}
        </div>
      </div>
    );

  return (
    <div>
      <PageHeader
        title="Reminders"
        subtitle="Follow-ups, payment chases & email reminders"
        action={
          <Button data-testid="add-reminder-button" onClick={() => setOpen(true)}>
            <Plus size={16} weight="bold" /> New Reminder
          </Button>
        }
      />
      <div className="p-8 space-y-8 max-w-3xl">
        {reminders.length === 0 && (
          <div className="card-soft p-12 text-center">
            <BellRinging size={40} weight="duotone" className="mx-auto text-[#F49B1C] mb-3" />
            <p className="text-slate-500 text-sm">No reminders yet. Create one to stay on top of follow-ups and payments.</p>
          </div>
        )}
        <Section title="Overdue" items={overdue} isOverdue accent="#B91C1C" />
        <Section title="Upcoming" items={upcoming} accent="#16205A" />
        <Section title="Completed" items={done} accent="#94A3B8" />
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New Reminder">
        <form onSubmit={save} className="space-y-4">
          <Field label="Title *" data-testid="reminder-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Chase payment INV-1001" />
          <div className="grid grid-cols-2 gap-3">
            <Select label="Type" data-testid="reminder-kind-select" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              <option value="follow_up">Follow up</option>
              <option value="payment">Payment</option>
              <option value="email">Email</option>
              <option value="general">General</option>
            </Select>
            <Field label="Due Date *" type="date" data-testid="reminder-date-input" required value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
          <Select label="Customer (optional)" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
            <option value="">None</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <TextArea label="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-reminder-button">Save</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
